import React, { useState, useRef } from 'react';
import { Control, Controller, Path } from 'react-hook-form';
import { Button, styled } from '@mui/material';
import { Row } from 'further-ui/components/Layout';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import { useNotification } from 'hooks/ui/useNotification';
import FormField from 'further-ui/components/FormField';
import { UserFormData } from '../types';
import Avatar from 'further-ui/components/Avatar';

const HiddenInput = styled('input')({
  display: 'none',
});

type Props<T extends UserFormData> = {
  control: Control<T>;
  displayName: string;
  currentProfilePicUrl?: string;
};

const ProfilePictureUpload = <T extends UserFormData>({
  control,
  displayName,
  currentProfilePicUrl,
}: Props<T>): React.ReactElement => {
  const { error } = useNotification();
  const { loading: isUploading, uploadFile } = useFileUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview state for display - either the current URL or an in-memory blob
  const [profilePicPreview, setProfilePicPreview] = useState<
    string | undefined
  >(currentProfilePicUrl);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect =
    (onChange: (value: string) => void) =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const { data, error: uploadError } = await uploadFile(
        file,
        UploadType.UserImages,
      );

      if (data) {
        const preview = URL.createObjectURL(file);
        setProfilePicPreview(preview);
        onChange(data.filePath);
      } else {
        error(uploadError.message);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

  return (
    <Controller
      name={'profilePic' as Path<T>}
      control={control}
      render={({ field: { onChange } }) => {
        return (
          <FormField label="User avatar">
            <Row spacing="md" centered>
              <Avatar
                text={displayName}
                src={profilePicPreview}
                size="xl"
                color={displayName && !profilePicPreview ? 'random' : undefined}
              />
              <HiddenInput
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileSelect(onChange)}
              />
              <Button
                variant="outlined"
                onClick={handleUploadClick}
                disabled={isUploading}
                size="large"
              >
                {isUploading ? 'Uploading...' : 'Upload image'}
              </Button>
            </Row>
          </FormField>
        );
      }}
    />
  );
};

export default ProfilePictureUpload;
